import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, wanneer } from '../i18n.js';
import { St } from './Status.jsx';

/**
 * De toegangsregels van een FTD, alleen lezen (docs/besluiten/bestaande-toegangsbeleiden.md): per regel van,
 * naar, poorten, IPS, logging, hits en wat opvalt. ZERO wijzigt de regels niet; een engineer beslist in de
 * FMC. Leest live van de FMC: alleen op verzoek.
 */
/* Kleine letters: de controle op vertalingen leest de labels via t() hieronder, niet de soorten. */
const bevinding = {
  alles: { label: 'Staat alles toe', soort: 'serious' },
  zonderIps: { label: 'Toegestaan zonder IPS', soort: 'warning' },
  ipsDetectie: { label: 'IPS alleen detectie', soort: 'warning' },
  geenLog: { label: 'Logt niet', soort: 'warning' },
  trust: { label: 'Trust (geen inspectie)', soort: 'neutral' },
  nietGeraakt: { label: 'Niet geraakt', soort: 'neutral' },
  uit: { label: 'Uitgeschakeld', soort: 'neutral' },
};
const kort = (lijst) => (lijst.length ? (lijst.length > 2 ? `${lijst.slice(0, 2).join(', ')} +${lijst.length - 2}` : lijst.join(', ')) : t('alles'));

export default function ToegangsRegels({ bron }) {
  const [vraag, zetVraag] = useState(false);
  const [filter, zetFilter] = useState('bevindingen');
  const q = useQuery({ queryKey: ['toegangsregels', bron.id], queryFn: () => api.toegangsregels(bron.id), enabled: vraag, retry: false });
  const d = q.data?.data;
  const lijst = d?.available ? d.rules.filter((r) => (filter === 'alle' ? true : filter === 'bevindingen' ? r.findings.some((f) => f !== 'nietGeraakt' && f !== 'uit') : r.findings.includes(filter))) : [];

  return (
    <section className="card">
      <header><h2>{t('Toegangsregels')}</h2>
        <span className="meta"><button type="button" className="namelink" disabled={q.isFetching} onClick={() => (vraag ? q.refetch() : zetVraag(true))}>
          {q.isFetching ? t('Bezig met lezen van de FMC…') : vraag ? t('Opnieuw lezen') : t('Lezen van de FMC')}</button></span></header>
      {q.isError && <div className="melder rood">{t(q.error.message)}</div>}
      {d && !d.available && <p className="empty">{t(d.reason)}</p>}
      {!vraag && <p className="soft">{t('Alleen lezen: ADTS ZERO laat de toegangsregels met rust en laat zien wat opvalt. Wijzigen doet een engineer in de FMC.')}</p>}
      {d?.available && (
        <>
          <dl className="facts">
            <dt>{t('Toegangsbeleid')}</dt><dd className="mono">{d.policy.name} <span className="muted">· {d.devices.map((x) => x.name).join(', ')}</span></dd>
            <dt>{t('Standaardactie')}</dt><dd className="mono">{d.policy.defaultAction ?? '—'}{['TRUST', 'NETWORK_DISCOVERY', 'PERMIT'].includes(d.policy.defaultAction) && <> <St soort="serious">{t('laat door wat geen regel raakt')}</St></>}</dd>
            <dt>{t('Regels')}</dt><dd>{getal(d.rules.length)} <span className="muted mono">· {Object.entries(d.actions).map(([a, n]) => `${a} ${n}`).join(' · ')}</span></dd>
            {d.hitsSince && <><dt>{t('Hits')}</dt><dd className="soft">{t('geteld sinds de laatste uitrol, {wanneer}', { wanneer: wanneer(d.hitsSince) })}</dd></>}
          </dl>
          <div className="filters" style={{ margin: '12px 0 8px' }}>
            <button type="button" className="chip" aria-pressed={filter === 'bevindingen'} onClick={() => zetFilter('bevindingen')}>{t('Met bevindingen')}</button>
            {Object.entries(bevinding).map(([k, b]) => d.counts[k] > 0 && (
              <button key={k} type="button" className="chip" aria-pressed={filter === k} onClick={() => zetFilter(k)}>{t(b.label)} <span className="tel">{getal(d.counts[k])}</span></button>
            ))}
            <button type="button" className="chip" aria-pressed={filter === 'alle'} onClick={() => zetFilter('alle')}>{t('Alle regels')} <span className="tel">{getal(d.rules.length)}</span></button>
          </div>
          {lijst.length ? (
            <table className="regels">
              <thead><tr><th>#</th><th>{t('Regel')}</th><th>{t('Van → naar')}</th><th>{t('Hits')}</th><th>{t('Bevindingen')}</th></tr></thead>
              <tbody>{lijst.map((r) => (
                <tr key={r.id} className={r.enabled ? undefined : 'muted'}>
                  <td className="mono muted">{r.index ?? ''}</td>
                  <td><span className="mono">{r.name}</span><br /><span className="muted mono" style={{ fontSize: 12 }}>{r.action}{r.category ? ` · ${r.category}` : ''}{r.ips ? ` · ${r.ips.name} (${r.ips.mode ?? '?'})` : ''}</span></td>
                  <td style={{ fontSize: 12.5 }}>{kort([...r.from.zones, ...r.from.networks])} → {kort([...r.to.zones, ...r.to.networks])}
                    {(r.to.ports.length > 0 || r.applications.length > 0) && <><br /><span className="muted mono">{kort([...r.to.ports, ...r.applications])}</span></>}</td>
                  <td className="mono">{r.hits === null ? '—' : getal(r.hits)}</td>
                  <td><span className="bevindingen">{r.findings.map((f) => <St key={f} soort={bevinding[f].soort}>{t(bevinding[f].label)}</St>)}</span></td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p><St soort="good">{t('Geen regels met deze bevinding')}</St></p>}
          <p className="noot">{t('Op allow- en trust-regels staat syslog bewust uit (te veel volume); dat is geen bevinding. ADTS ZERO wijzigt die regels niet — alleen op block-regels (deny) zet de controleslag hierboven syslog aan.')}</p>
        </>
      )}
    </section>
  );
}
